import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col, DropdownItem, DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Row,
  Table, UncontrolledButtonDropdown
} from "reactstrap"
import { NIL } from "uuid"
import DatePicker from "react-datepicker"
import Select from "react-select"
import { customSingleValue, formatItemOptionLabel, getMaxValue } from "../../helpers/utils"
import { getProductRequest } from "../../store/product/saga"
import { debounce } from "lodash"
import { getProductUnitOfMeasurementsRequest } from "../../store/product-unit-of-measurement/saga"
import ProductStockInRow from "./ProductStockInRow"
import moment from "moment/moment"
import ProductStockInConfirmModal from "./ProductStockInConfirmModal"
import PrintPreviewModal from "./PrintPreviewModal"

const ProductStockInForm = props => {
  const { item, onSubmit, onCancel } = props

  const [ term, setTerm ] = useState('')
  const [ loading, setLoading ] = useState(false)
  const [ isHeaderSelected, setIsHeaderSelected ] = useState(false)
  const [ itemOptions, setItemOptions ] = useState([])
  const [ modalConfirmStock, setModalConfirmStock ] = useState(false)
  const [ modalPrintPreview, setModalPrintPreview ] = useState(false)
  const [ locationOptions, setLocationOptions ] = useState([])

  const [ productStockIn, setProductStockIn ] = useState({
    id: NIL,
    code: '',
    name: '',
    status: 0,
    date: new Date(),
    productStockInProducts: []
  })

  const [ isSubmitted, setIsSubmitted ] = useState(false)


  const debouncedQuickSearch = useCallback(
      debounce(term => {
        setTerm(term)
      }, 500),
      []
  )

  const handleInputChange = (value) => {
    debouncedQuickSearch(value)
  }

  useEffect(() => {
    if (item) {
      setProductStockIn({
        ...item,
        date: moment(item.date).toDate(),
        productStockInProducts: item.productStockInProducts ? item.productStockInProducts.map((a, index) => {
          return {
            ...a,
            index,
            label: `${ a.productCode } - ${ a.productName }`,
            productId: {
              key: a.productId,
              value: a.productId,
            },
            unitOfMeasurementId: {
              key: a.unitOfMeasurementId,
              value: a.unitOfMeasurementId,
              label: a.unitOfMeasurementName
            }
          }
        }) : [],
      })

    } else {
      setProductStockIn({
        id: NIL,
        code: '',
        name: '',
        date: new Date(),
        status: 0,
        productStockInProducts: []
      })
    }
  }, [ item ])

  useEffect(() => {
    setLoading(true)
    getProductRequest({ term, page: 1, pageSize: 20 }).then(res => {
      if (res.data) {
        setLoading(false)
        setItemOptions(res.data.map((a, index) => {
          return {
            key: a.id,
            value: a.id,
            type: a.type,
            itemAttachment: a.productImages.length > 0 ? a.productImages[0] : null,
            quantity: a.onHandQuantity,
            label: `${ a.code } - ${ a.name }`
          }
        }))
      }
    })
  }, [ term ])

  const handleSelectChange = (valueType, actionMeta) => {
    const { name } = actionMeta

    switch (name) {
      case 'productId':
        if (valueType) {
          const { value, label } = valueType
          getProductUnitOfMeasurementsRequest({ productId: value }).then(res => {
            const { data } = res
            let uomOptions = data?.map(a => {
              return {
                value: a.unitOfMeasurementId,
                label: a.unitOfMeasurementName
              }
            }) ?? []

            let max = 0
            if (productStockIn.productStockInProducts.length > 0) {
              max = getMaxValue(
                  productStockIn.productStockInProducts.map(a => {
                    return a.index
                  })
              )
              max += 1
            } else {
              max = max + 1
            }

            let newEntry = {
              index: max,
              id: NIL,
              productId: valueType,
              label: label,
              quantity: 0,
              locationId: null,
              unitOfMeasurementId: uomOptions.length > 0 ? uomOptions[0] : null,
              uomOptions,
              isSelected: false
            }

            setProductStockIn({
              ...productStockIn,
              productStockInProducts: [ ...productStockIn.productStockInProducts, newEntry ]
            })
          })
        }
        break
    }
  }

  const handleDeleteSelected = () => {
    setProductStockIn({
      ...productStockIn,
      productStockInProducts: productStockIn.productStockInProducts.filter(e => !e.isSelected)
    })
    setIsHeaderSelected(false)
  }

  const handleHeaderSelect = e => {
    const { checked } = e.target
    setIsHeaderSelected(checked)
    setProductStockIn({
      ...productStockIn,
      productStockInProducts: productStockIn.productStockInProducts.map(entry => {
        return {
          ...entry,
          isSelected: checked
        }
      })
    })
  }

  const handleStockInItemChange = entry => {
    setProductStockIn({
      ...productStockIn,
      productStockInProducts: productStockIn.productStockInProducts.map(a => a.index === entry.index ? entry : a)
    })
  }

  const handleDeleteStockInItem = (entry) => {
    setProductStockIn({
      ...productStockIn,
      productStockInProducts: productStockIn.productStockInProducts.filter(e => e.index !== entry.index)
    })
  }

  const handleOnChange = (e) => {
    const { name, value } = e.target
    setProductStockIn({
      ...productStockIn,
      [name]: value
    })
  }

  const handleOnDateChange = (name, value) => {
    setProductStockIn({
      ...productStockIn,
      [name]: value
    })
  }

  const handleSubmit = () => {
    setIsSubmitted(true)
    let isValid = productStockIn.date && productStockIn.name
    let hasNull = productStockIn.productStockInProducts.some(e => !e.unitOfMeasurementId || e.quantity === 0)


    if (!hasNull && isValid) {
      let data = {
        ...productStockIn,
        date: productStockIn.date?.toISOString(),
        productStockInProducts: productStockIn.productStockInProducts.map(entry => {
          return {
            ...entry,
            productId: entry.productId?.value,
            unitOfMeasurementId: entry.unitOfMeasurementId?.value
          }
        })
      }

      onSubmit(data)
    }
  }

  const handleSubmitStockConfirmation = () => {
    setModalConfirmStock(false)
    setIsSubmitted(true)
    let isValid = productStockIn.date && productStockIn.name
    let hasNull = productStockIn.productStockInProducts.some(e => !e.unitOfMeasurementId || e.quantity === 0)


    if (!hasNull && isValid && productStockIn.productStockInProducts.length > 0) {
      let data = {
        ...productStockIn,
        status: 1,
        date: productStockIn.date?.toISOString(),
        productStockInProducts: productStockIn.productStockInProducts.map(entry => {
          return {
            ...entry,
            productId: entry.productId?.value,
            unitOfMeasurementId: entry.unitOfMeasurementId?.value
          }
        })
      }

      onSubmit(data)
    }
  }


  return (
      <>
        <Card className={ "mb-2" }>
          <CardHeader className={ "bg-transparent border-bottom" }>
            <Row>
              <Col>
                <CardTitle>Info</CardTitle>
              </Col>
              <Col className={ "text-end" }>
                {
                  productStockIn.id !== NIL ?
                      <UncontrolledButtonDropdown>
                        <DropdownToggle caret color="primary" outline>
                          <i className="mdi mdi-dots-vertical"></i>
                        </DropdownToggle>
                        <DropdownMenu className="dropdown-menu-end">
                          <DropdownItem
                              onClick={ () => setModalPrintPreview(true) }
                              className="text-primary"
                          >
                            <i className="mdi mdi-printer me-1"/> Print Preview
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledButtonDropdown> : null
                }
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Row>
              <Col>
                <FormGroup className="mb-3">
                  <Label htmlFor="">Code</Label>
                  <Input
                      type="text"
                      name="code"
                      placeholder="Auto Generate"
                      value={ productStockIn.code }
                      readOnly
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-3">
                  <Label htmlFor="">Title</Label>
                  <Input
                      type="text"
                      name="name"
                      placeholder="Title"
                      autoComplete="off"
                      className={ isSubmitted && !productStockIn.name ? "is-invalid" : "" }
                      value={ productStockIn.name }
                      onChange={ handleOnChange }
                  />
                  { isSubmitted && !productStockIn.name && (
                      <div className="invalid-feedback-custom">Name is required.</div>
                  ) }
                </FormGroup>
              </Col>
              <Col>
                <FormGroup
                    className={
                        "select2-container" + (isSubmitted && !productStockIn.date ? " is-invalid" : "")
                    }
                >
                  <Label>Stock In Date</Label>
                  <DatePicker
                      className="form-control"
                      selectsStart
                      name="date"
                      selected={ productStockIn.date }
                      onChange={ obj => handleOnDateChange('date', obj) }
                      dateFormat="dd-MMM-yyyy"
                      placeholderText="Stock In Date"
                      isClearable
                  />
                </FormGroup>
                { isSubmitted && !productStockIn.date && (
                    <div className="invalid-feedback-custom">
                      Stock In Date is required.
                    </div>
                ) }
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Card className={ "mb-2" }>
          <CardHeader className={ "bg-transparent border-bottom" }>
            <Row>
              <Col md={ 2 }>
                <CardTitle className={ "pt-2" }>Items</CardTitle>
              </Col>
              <Col>
              </Col>
            </Row>
          </CardHeader>
          <CardHeader className={ "bg-transparent border-bottom" }>
            <Row>
              <Col>
                <Label className={ "mt-2" }>Find Items:</Label>
              </Col>
              <Col md={ 8 }>
                <Select
                    name="productId"
                    value={ null }
                    placeholder={ "Find by Code, Name, ..." }
                    onChange={ handleSelectChange }
                    options={ itemOptions }
                    components={ {
                      SingleValue: customSingleValue
                    } }
                    onInputChange={ handleInputChange }
                    formatOptionLabel={ formatItemOptionLabel }
                    classNamePrefix="select2-selection"
                    isLoading={ loading }
                    isClearable
                />
              </Col>
              <Col>
                <div className="text-sm-end">
                  <Button color={ "danger" }
                          onClick={ handleDeleteSelected }
                          outline
                          disabled={ !productStockIn.productStockInProducts.some(e => e.isSelected) }
                  >
                    <i className="fas fa-trash me-1"/> Delete Selected
                  </Button>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Table
                id="tech-companies-1"
                className="table-editable table table-striped table-bordered table-nowrap"
            >
              <thead className={ "bg-primary text-white" }>
              <tr>
                <th className={ "text-center" } style={ { width: "80px" } }>
                  <input
                      type="checkbox"
                      className="form-check-input"
                      id="headerSelected"
                      checked={ isHeaderSelected }
                      onChange={ handleHeaderSelect }
                  />
                </th>
                <th className="text-center">Item</th>
                <th className={ "text-center" } style={ { width: "250px" } }>Quantity</th>
                <th className={ "text-center" } style={ { width: "250px" } }>UOM</th>
                <th className={ "text-center" } style={ { width: "120px" } }>Action</th>
              </tr>
              </thead>
              <tbody>

              {
                productStockIn.productStockInProducts.map((entry, index) => {
                  return <ProductStockInRow
                      key={ index }
                      item={ entry }
                      isSubmitted={ isSubmitted }
                      onChange={ handleStockInItemChange }
                      onDelete={ handleDeleteStockInItem }
                  />
                })
              }
              </tbody>
            </Table>
          </CardBody>
        </Card>

        <Card className={ "mb-2" }>
          <CardBody>
            {
                [ 0 ].includes(productStockIn.status) && // Draft
                <Button color="primary" className={ "me-1" }
                        onClick={ handleSubmit }
                        type="submit">
                  Submit
                </Button>
            }
            {
                productStockIn.id !== NIL && [ 0 ].includes(productStockIn.status) && productStockIn.productStockInProducts.length > 0 && // Draft
                <Button color="primary" className={ "me-1" }
                        onClick={ () => setModalConfirmStock(true) }
                        type="submit">
                  Confirm Into Stock
                </Button>
            }

            <Button color="secondary"
                    onClick={ () => onCancel() }
                    type="button">
              Cancel
            </Button>
          </CardBody>
        </Card>

        <ProductStockInConfirmModal
            title="Confirm item into stock"
            isOpen={ modalConfirmStock }
            toggle={ () => setModalConfirmStock(!modalConfirmStock) }
            onSubmit={ handleSubmitStockConfirmation }
        />

        <PrintPreviewModal
            isOpen={ modalPrintPreview }
            productStockInId={ productStockIn.id }
            toggle={ () => setModalPrintPreview(!modalPrintPreview) }
        />
      </>
  )
}

ProductStockInForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  item: PropTypes.object,
}

export default ProductStockInForm